import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Paper, 
    Typography, 
    Grid,
    Card,
    CardContent,
    Divider,
    useTheme,
    CircularProgress,
    Alert,
    Tooltip,
    IconButton,
    Button,
    TextField,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    LinearProgress
} from '@mui/material';
import { 
    BarChart,
    PieChart
} from '@mui/x-charts';
import { formatDistanceToNow } from 'date-fns';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import api from '../../services/api';

const AffiliateDashboard = ({ affiliateStats }) => {
    const theme = useTheme();
    const [balance, setBalance] = useState(null);
    const [balanceLoading, setBalanceLoading] = useState(true);
    const [paymentInfo, setPaymentInfo] = useState({ paypalEmail: '', preferredPaymentMethod: 'paypal' });
    const [paymentInfoOpen, setPaymentInfoOpen] = useState(false);
    const [paymentInfoSaving, setPaymentInfoSaving] = useState(false);
    const [withdrawOpen, setWithdrawOpen] = useState(false);
    const [withdrawLoading, setWithdrawLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        const fetchBalance = async () => {
            try {
                const res = await api.get('/affiliate/balance');
                setBalance(res.data);
            } catch (err) {
                console.error('Failed to fetch balance:', err);
            } finally {
                setBalanceLoading(false);
            }
        };
        fetchBalance();
    }, []);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleSavePaymentInfo = async () => {
        setPaymentInfoSaving(true);
        try {
            await api.put('/affiliate/payment-info', {
                paypalEmail: paymentInfo.paypalEmail,
                preferredPaymentMethod: paymentInfo.preferredPaymentMethod,
            });
            showSnackbar('Payment information saved successfully.');
            setPaymentInfoOpen(false);
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Failed to save payment information.', 'error');
        } finally {
            setPaymentInfoSaving(false);
        }
    };

    const handleWithdraw = async () => {
        setWithdrawLoading(true);
        try {
            const res = await api.post('/affiliate/withdraw');
            showSnackbar(`Withdrawal of $${res.data.amount.toFixed(2)} has been queued for processing.`);
            setWithdrawOpen(false);
            const balRes = await api.get('/affiliate/balance');
            setBalance(balRes.data);
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Failed to process withdrawal.', 'error');
        } finally {
            setWithdrawLoading(false);
        }
    };

    if (!affiliateStats) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    const { referrals, earnings, code, username } = affiliateStats;

    // Handle missing or invalid data
    if (!referrals || !Array.isArray(referrals)) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                Error loading affiliate data. Please try again later.
            </Alert>
        );
    }

    // Calculate referral stats
    const totalReferrals = referrals.length;
    const activeReferrals = referrals.filter(ref => ref.user?.subscription?.status === 'active').length;
    const trialReferrals = referrals.filter(ref => ref.user?.subscription?.status === 'trialing').length;

    // Prepare data for pie chart
    const referralStatusData = [
        { id: 0, value: activeReferrals, label: 'Active', color: theme.palette.success.main },
        { id: 1, value: trialReferrals, label: 'Trial', color: theme.palette.info.main },
        { id: 2, value: totalReferrals - (activeReferrals + trialReferrals), label: 'Inactive', color: theme.palette.grey[400] }
    ].filter(item => item.value > 0); // Only show segments with values

    // Prepare monthly earnings data (last 6 months)
    const monthlyEarnings = Array.from({ length: 6 }, (_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const monthYear = date.toLocaleString('default', { month: 'short', year: '2-digit' });
        
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const amount = earnings?.monthly?.[monthKey] || 0;
        
        return {
            month: monthYear,
            amount: amount
        };
    }).reverse();

    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom>
                Affiliate Dashboard
            </Typography>
            
            {/* Stats Overview */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Referrals
                            </Typography>
                            <Typography variant="h4">
                                {totalReferrals}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="body2" color="textSecondary">
                                    Direct:
                                </Typography>
                                <Typography variant="body2">
                                    {referrals.filter(ref => ref.level === 1).length}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="textSecondary">
                                    Second-level:
                                </Typography>
                                <Typography variant="body2">
                                    {referrals.filter(ref => ref.level === 2).length}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Active Subscribers
                            </Typography>
                            <Typography variant="h4">
                                {activeReferrals}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="body2" color="textSecondary">
                                    Conversion rate:
                                </Typography>
                                <Typography variant="body2">
                                    {totalReferrals > 0 ? ((activeReferrals / totalReferrals) * 100).toFixed(1) : 0}%
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Earnings
                            </Typography>
                            <Typography variant="h4">
                                ${earnings.total?.toFixed(2)}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="body2" color="textSecondary">
                                    Direct:
                                </Typography>
                                <Typography variant="body2">
                                    ${earnings.direct?.toFixed(2) || '0.00'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="textSecondary">
                                    Second-level:
                                </Typography>
                                <Typography variant="body2">
                                    ${earnings.secondLevel?.toFixed(2) || '0.00'}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Affiliate Link
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {window.location.origin}?ref={username || code || ''}
                                </Typography>
                                <Tooltip title="Copy link">
                                    <IconButton 
                                        size="small" 
                                        onClick={() => {
                                            navigator.clipboard.writeText(`${window.location.origin}?ref=${username || code || ''}`);
                                        }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Referral Status
                        </Typography>
                        <Box sx={{ height: 300 }}>
                            <PieChart
                                series={[
                                    {
                                        data: referralStatusData,
                                        highlightScope: { faded: 'global', highlighted: 'item' },
                                        faded: { innerRadius: 30, additionalRadius: -30 },
                                    },
                                ]}
                                height={300}
                            />
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Monthly Earnings
                        </Typography>
                        <Box sx={{ height: 300 }}>
                            <BarChart
                                xAxis={[{ 
                                    scaleType: 'band', 
                                    data: monthlyEarnings.map(item => item.month) 
                                }]}
                                series={[
                                    { 
                                        data: monthlyEarnings.map(item => item.amount),
                                        color: theme.palette.primary.main
                                    }
                                ]}
                                height={300}
                            />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Recent Referrals */}
            <Paper sx={{ mt: 4, p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Recent Referrals
                </Typography>
                <Box>
                    {referrals.length > 0 ? (
                        referrals.slice(0, 10).map((referral, index) => (
                            <React.Fragment key={referral.user._id || index}>
                                <Box sx={{ py: 1 }}>
                                    <Grid container alignItems="center" spacing={2}>
                                        <Grid item xs={12} sm={3}>
                                            <Typography>
                                                {referral.user.username}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Typography color="textSecondary">
                                                Joined {formatDistanceToNow(new Date(referral.user.profile?.joinedDate || referral.joinedDate))} ago
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3} sm={2}>
                                            <Typography sx={{ 
                                                color: referral.user.subscription?.status === 'active' 
                                                    ? 'success.main' 
                                                    : referral.user.subscription?.status === 'trialing'
                                                    ? 'info.main'
                                                    : 'text.secondary'
                                            }}>
                                                {referral.user.subscription?.status 
                                                    ? referral.user.subscription.status.charAt(0).toUpperCase() + 
                                                      referral.user.subscription.status.slice(1)
                                                    : 'Inactive'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3} sm={2}>
                                            <Typography 
                                                color={referral.level === 1 ? "primary" : "secondary"}
                                                fontWeight="medium"
                                            >
                                                {referral.level === 1 ? "Direct" : "Second-level"}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                            <Typography sx={{ fontWeight: 'medium' }}>
                                                ${referral.commission?.toFixed(2) || '0.00'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                                {index < referrals.length - 1 && <Divider />}
                            </React.Fragment>
                        ))
                    ) : (
                        <Box sx={{ py: 3, textAlign: 'center' }}>
                            <Typography color="textSecondary">
                                No referrals yet. Share your affiliate link to start earning!
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Withdrawal Section */}
            <Paper sx={{ mt: 4, p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Withdrawals
                </Typography>
                {balanceLoading ? (
                    <LinearProgress sx={{ my: 2 }} />
                ) : (
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <Box>
                                <Typography variant="body2" color="textSecondary">Available Balance</Typography>
                                <Typography variant="h4" color="success.main">
                                    ${(balance?.available || 0).toFixed(2)}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Box>
                                <Typography variant="body2" color="textSecondary">Pending (Holding Period)</Typography>
                                <Typography variant="h5">
                                    ${(balance?.pending || 0).toFixed(2)}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Box>
                                <Typography variant="body2" color="textSecondary">Total Paid Out</Typography>
                                <Typography variant="h5">
                                    ${(balance?.paid || 0).toFixed(2)}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setPaymentInfoOpen(true)}
                                >
                                    Update Payment Info
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    disabled={(balance?.available || 0) < 50}
                                    onClick={() => setWithdrawOpen(true)}
                                >
                                    Request Withdrawal
                                </Button>
                                {(balance?.available || 0) < 50 && (
                                    <Typography variant="caption" color="textSecondary" sx={{ alignSelf: 'center' }}>
                                        Minimum withdrawal is $50. Current available: ${(balance?.available || 0).toFixed(2)}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Paper>

            {/* Payment Info Dialog */}
            <Dialog open={paymentInfoOpen} onClose={() => setPaymentInfoOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Update Payment Information</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="PayPal Email"
                            type="email"
                            value={paymentInfo.paypalEmail}
                            onChange={(e) => setPaymentInfo({ ...paymentInfo, paypalEmail: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            label="Preferred Payment Method"
                            select
                            value={paymentInfo.preferredPaymentMethod}
                            onChange={(e) => setPaymentInfo({ ...paymentInfo, preferredPaymentMethod: e.target.value })}
                            fullWidth
                        >
                            <MenuItem value="paypal">PayPal</MenuItem>
                            <MenuItem value="bank">Bank Transfer</MenuItem>
                            <MenuItem value="store_credit">Store Credit (+5% bonus)</MenuItem>
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPaymentInfoOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSavePaymentInfo}
                        disabled={paymentInfoSaving}
                    >
                        {paymentInfoSaving ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Withdrawal Confirmation Dialog */}
            <Dialog open={withdrawOpen} onClose={() => setWithdrawOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm Withdrawal</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        You are about to request a withdrawal of <strong>${(balance?.available || 0).toFixed(2)}</strong>.
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        This will queue all available commissions for processing. You will receive your payment via your selected payment method.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setWithdrawOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleWithdraw}
                        disabled={withdrawLoading}
                    >
                        {withdrawLoading ? <CircularProgress size={24} /> : 'Confirm Withdrawal'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AffiliateDashboard;
